import React, { useCallback, useEffect, useState } from "react";
import { Switch, Drawer, Tabs, Upload, Badge, Button as AntdButton, Tooltip, Input as AntdInput, Rate, DatePicker, Checkbox } from 'antd';
import { Button, Input, Row, Col } from "reactstrap";
import Select from 'react-select';
import { FireFilled, InboxOutlined, PaperClipOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined, PlusCircleOutlined, ArrowLeftOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { toastr } from "react-redux-toastr";
import general_fetch from "../../utils/fetch";
import { colourStyles4 } from "../../utils/react_select_styles";
import { date_string } from "../../utils/date_string";
import { convert_to_readable } from "../../utils/convert_to_readable";
import timeSince from "../../utils/timeSince";
import { user_details_fetched } from "../../actions";
import { customer_tickets_status_options } from "../../utils/customer_tickets_status_options";
import { confirmAlert } from "react-confirm-alert";
import DeleteNotification from "../../components/DeleteNotification";
import NA from "../../components/NA";
import {impact_area_options} from "../../utils/cs_ticket_impact_area";
import { DEFAULT_PRIORITY, MAX_PRIORITY } from "../../utils/priority_options";
import dayjs from 'dayjs';
import {ticketChecklist} from "../../utils/ticket_checklist";
import CommentModal from "../../components/CommentModal";


const { Dragger } = Upload;

// Constants
const MAX_DESCRIPTION_LENGTH = 5000;

// Helper function to generate unique IDs
let uniqueIdCounter = 0;
const generateUniqueId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    uniqueIdCounter += 1;
    return `${Date.now()}-${uniqueIdCounter}`;
};

const parse_org_info = (org_info) => {
    try {
        let org_ids = JSON.parse(org_info).map(x => x.id);
        return org_ids;
    } catch(err) {
        return [];
    }
}

/**
 * Converts Google Space thread link to full URL format
 * @param {string|null|undefined} threadLink - The thread link from cs_team_doubts_thread field
 * @returns {string|null} - Converted URL or null if no valid link
 *
 * Handles three cases:
 * 1. Already a full URL (http/https) - returns as-is
 * 2. Pattern "spaces/[id]/threads/[id]" - converts to full Google Chat URL
 * 3. Invalid/malformed values - returns null to prevent backend validation errors
 */
const convertGoogleSpaceThreadToUrl = (threadLink) => {
    if (!threadLink) {
        return null;
    }

    const rawThread = String(threadLink).trim();

    // If it's already a full URL, use it as-is
    if (/^https?:\/\//i.test(rawThread)) {
        return rawThread;
    }

    // If it matches the expected "spaces/[id]/threads/[id]" pattern, convert it
    if (/^spaces\/[^/]+\/threads\/[^/]+$/i.test(rawThread)) {
        return rawThread.replace(
            /spaces\/([^/]+)\/threads\/([^/]+)/i,
            "https://chat.google.com/room/$1/$2/$2"
        );
    }

    // Return null for unrecognized patterns to prevent backend validation errors
    // This handles malformed data gracefully without causing API failures
    return null;
}

const disabledDate = (current) => {
  return current && dayjs(current).day() != 1;
};

const order_org_id_options = (values) => {
  return values.filter((v) => !v.editable).concat(values.filter((v) => v.editable));
};

const org_id_option_style = {
    multiValue: (base, state) => {
        return state.data.editable ? base : { ...base, backgroundColor: 'gray' };
    },
    multiValueLabel: (base, state) => {
        return state.data.editable
            ? base
            : { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 };
    },
    multiValueRemove: (base, state) => {
        return state.data.editable ? base : { ...base, display: 'none' };
    },
}

const submit = (yes_del, no_del, message, button_name, Custom_modal) => {
	confirmAlert({
	  title: 'Confirm to submit',
	  message: message,
	  customUI: ({ onClose }) => {
		return (
		  Custom_modal ?
			<div>
			  <Custom_modal yes_del={yes_del} no_del={no_del} onClose={onClose} text={message} button_name={button_name} />
			</div>
			:
			<div className='custom-ui'>
			  <DeleteNotification yes_del={yes_del} no_del={no_del} onClose={onClose} text={message} button_name={button_name} />
			</div>
		);
	  }
	});
}

const check_all_org_setting_access = ({user_details, team_info}) => {
    if(user_details && user_details.role && user_details.role.store && user_details.role.store.includes('customer_success_admin')) {
        return true
    }
    // product manager can edit any ticket
    if(user_details && user_details.role && user_details.role.store && user_details.role.store.includes('infurnia_product_manager')) {
        return true
    }
    // all team leads can edit any ticket
    if(team_info && user_details && user_details.id) {
        for(let user_id in team_info) {
            if(team_info[user_id].team_lead == user_details.id) {
                return true;
            }
        }
    }
    return false;
}

const check_ticket_edit_access = ({user_details, selected_ticket, team_info}) => {
    // customer success admin can edit any ticket
    if(user_details && user_details.role && user_details.role.store && user_details.role.store.includes('customer_success_admin')) {
        return true
    }
    // product manager can edit any ticket
    if(user_details && user_details.role && user_details.role.store && user_details.role.store.includes('infurnia_product_manager')) {
        return true
    }
    // all team leads can edit any ticket
    if(team_info && user_details && user_details.id) {
        for(let user_id in team_info) {
            if(team_info[user_id].team_lead == user_details.id) {
                return true;
            }
        }
    }

    // team lead of the creator's team can edit that ticket
    /* if(team_info && selected_ticket && selected_ticket.creator_id && user_details && user_details.id) {
        let this_team = team_info[selected_ticket.creator_id]
        if(this_team) {
            if(this_team.team_lead == user_details.id) {
                return true
            }
        }
    } */
    // the creator of the ticket can edit the ticket
    if(selected_ticket && selected_ticket.creator_id && user_details && user_details.id && (user_details.id == selected_ticket.creator_id)) {
        return true
    }
    return false
}

const jira_project_options = [
    {value: 'PT', label: 'Product Team'},
    {value: 'AP', label: 'Applications'},
    {value: 'CT', label: 'Core Tech'},
    {value: 'GK', label: 'Geometric Kernel'},
    {value: 'GR', label: 'Graphics'},
    {value: 'ST', label: 'Server Tech'},
    {value: 'CSP', label: 'Customer Success Portal'},
    {value: 'MES', label: 'MES'},
    {value: 'AD', label: 'Admin Portal'},
];

const jira_priority_options = [
	{value: 'Highest', label: 'Highest'},
	{value: 'High', label: 'High'},
	{value: 'Medium', label: 'Medium'},
	{value: 'Low', label: 'Low'},
	{value: 'Lowest', label: 'Lowest'},	
];


const jira_type_options = [
    {label: "Epic", value: "Epic"},
    {label: "Task", value: "Task"},
    {label: "Bug", value: "Bug"},
    {label: "QA Bug", value: "QA Bug"},
];

const jira_assignee_options = [
    {label: "Nikhil Kumar", value: "557058:6bdffd8b-34b3-41be-9c6a-6a303ade9b17"},
    {label: "Koushik Challa", value: "557058:befb16fb-2151-4a28-a34d-aa5596714d41"},
    {label: "Akash Dubey", value: "557058:f2cff7a8-b59e-4c12-bfff-6176dd4e4e18"},
    //{label: "Akshat Jain", value: "603de5691c44230072ef894d"},
    {label: "Shamit Monga", value: "604b22adb020eb0068683772"},
    {label: "Bhargav Siva Phaneendra Annavarapu", value: "62bd820a268cac6e31c53572"},
    //{label: "Dishank Goel", value: "62bd820c118b20bee2bca5d1"},
    {label: "Sai Krishna", value: "62bd820efa171a27239e370f"},
    //{label: "lokesh.kumar", value: "62d535d3d1bb05497b0139bf"},
    //{label: "Atharva Varde", value: "712020:2d094dbd-32f2-438b-8df1-257d4817555f"},
    {label: "Parth Ajmera", value: "712020:84e9b1ff-2767-4b03-881f-93ef78021667"},
    {label: "Vedant Saboo", value: "712020:781c7956-ad9c-4007-85bd-9bda51ea7eca"},
    {label: "Vinayak Bharadwaj", value: "712020:2961bded-bd18-4f9e-9e39-5a582ea98e44"},
    {label: "Yash Basrani", value: "712020:df35cec5-d9c0-4605-8871-f85dd12c08ac"},
];

// Reusable component for rendering file/link items with description editing
const FileItem = ({
	file,
	idx,
	isGlobalPath,
	viewMode,
	editingFileId,
	editDescription,
	onEditStart,
	onEditCancel,
	onEditSave,
	onEditDescriptionChange,
	onDelete,
	isAnyFileBeingEdited
}) => {
	const hasDescription = file.file_description && file.file_description.trim() !== '';
	const isBeingEdited = editingFileId === file.id;

	const getFileUrl = () => {
		return isGlobalPath
			? file.file_path
			: `https://storage.googleapis.com/${global.config.ticketing_bucket_name}${file.file_path}`;
	};

	const getFileName = () => {
		return isGlobalPath
			? file.file_path
			: `file_${idx + 1} (click to view)`;
	};

	const handleEditClick = () => {
		if (isAnyFileBeingEdited && !isBeingEdited) {
			// Another file is being edited - show warning
			submit(
				() => onEditStart(file.id, file.file_description || ''),
				() => {},
				'You have unsaved changes on another file. Switching files will discard those changes. Continue?',
				'Continue',
				''
			);
		} else {
			onEditStart(file.id, file.file_description || '');
		}
	};

	const handleDeleteClick = () => {
		submit(
			() => {
				// Clean up editing state if this file is being edited
				if (editingFileId === file.id) {
					onEditCancel();
				}
				onDelete(file.id);
			},
			() => {},
			'This action will delete the file, are you sure you want to proceed?',
			'',
			''
		);
	};

	// Determine if edit icon should be disabled
	const isEditDisabled = isAnyFileBeingEdited && !isBeingEdited;

	return (
		<div className="mb-2" key={file.id}>
			<div className="flex_property mb-1" style={{
				padding: '8px',
				borderRadius: '4px',
				border: !hasDescription && !viewMode ? '2px solid #ff4d4f' : '1px solid #f0f0f0',
				backgroundColor: !hasDescription && !viewMode ? '#fff1f0' : 'transparent'
			}}>
				<PaperClipOutlined className='mr-2' style={{ opacity: '0.5' }} />
				<div style={{display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0}}>
					<a
						style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:"nowrap"}}
						href={getFileUrl()}
						target="_blank"
						rel="noopener noreferrer"
					>
						{getFileName()}
					</a>
					{isBeingEdited ? (
						<div style={{marginTop: '4px'}}>
							<div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
								<AntdInput
									type="text"
									placeholder="Description is required *"
									value={editDescription}
									onChange={(e) => {
										const value = e.target.value;
										if (value.length > MAX_DESCRIPTION_LENGTH) {
											toastr.warning(`Description too long (max ${MAX_DESCRIPTION_LENGTH} characters)`, {
												timeOut: 3000
											});
											return;
										}
										onEditDescriptionChange(value);
									}}
									aria-required="true"
									aria-invalid={!editDescription.trim() ? "true" : "false"}
									style={{ height: '32px', flex: 1, borderColor: !editDescription.trim() ? '#ff4d4f' : undefined }}
									maxLength={MAX_DESCRIPTION_LENGTH}
								/>
								<SaveOutlined
									className="cp"
									role="button"
									tabIndex={0}
									aria-label="Save description"
									style={{color: 'green', fontSize: '16px'}}
									onClick={() => onEditSave(file.id, editDescription)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											onEditSave(file.id, editDescription);
										}
									}}
								/>
								<CloseOutlined
									className="cp"
									role="button"
									tabIndex={0}
									aria-label="Cancel editing"
									style={{color: 'red', fontSize: '16px'}}
									onClick={onEditCancel}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											onEditCancel();
										}
									}}
								/>
							</div>
							{editDescription && (
								<div style={{
									fontSize: '11px',
									color: '#888',
									marginTop: '2px',
									textAlign: 'right'
								}}>
									{editDescription.length}/{MAX_DESCRIPTION_LENGTH}
								</div>
							)}
						</div>
					) : (
						<div style={{
							fontSize: '12px',
							marginTop: '2px',
							color: !hasDescription ? '#ff4d4f' : 'rgba(0, 0, 0, 0.65)',
							fontWeight: !hasDescription ? 'bold' : 'normal'
						}}>
							{file.file_description || '⚠️ Description required'}
						</div>
					)}
				</div>
				{viewMode ? '' : (
					<div style={{display: 'flex', gap: '8px', alignItems: 'flex-start'}}>
						{!isBeingEdited && (
							<Tooltip title={isEditDisabled ? 'Finish editing other file first' : 'Edit description'}>
								<EditOutlined
									className="cp"
									role="button"
									tabIndex={isEditDisabled ? -1 : 0}
									aria-label={isEditDisabled ? 'Edit description (disabled - finish editing other file first)' : 'Edit description'}
									aria-disabled={isEditDisabled}
									style={{
										color: !hasDescription ? '#ff4d4f' : '#1890ff',
										opacity: isEditDisabled ? 0.3 : 1,
										cursor: isEditDisabled ? 'not-allowed' : 'pointer'
									}}
									onClick={isEditDisabled ? undefined : handleEditClick}
									onKeyDown={(e) => {
										if (!isEditDisabled && (e.key === 'Enter' || e.key === ' ')) {
											e.preventDefault();
											handleEditClick();
										}
									}}
								/>
							</Tooltip>
						)}
						<DeleteOutlined
							className="cp"
							role="button"
							tabIndex={0}
							aria-label="Delete file"
							style={{color:'red'}}
							onClick={handleDeleteClick}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleDeleteClick();
								}
							}}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

const EditCustomerTicket = ({ open, toggle, all_orgs, current_org, selected_ticket_id, fetch_customer_tickets, set_page_loader, set_view_mode_true, cs_org_users, user_details, team_info }) => {

	const [title, set_title] = useState();
    const [impact_area, set_impact_area] = useState();
	const [type, set_type] = useState();
	// const [org_id, set_org_id] = useState();
    const [org_ids, set_org_ids] = useState([]);
    const [org_ids_options, set_org_ids_options] = useState([]);
	const [priority, set_priority] = useState();
	const [expected_resolution_time, set_expected_resolution_time] = useState();
	const [assignee_id, set_assignee_id] = useState();
    const [creator_id, set_creator_id] = useState();
	// const [customer_priority, set_customer_priority] = useState();
	const [client_notification, set_client_notification] = useState(true);
	const [auto_close, set_auto_close] = useState(true);
	const [description, set_description] = useState();
	const [design_link, set_design_link] = useState();
	const [status, set_status] = useState();
	const [archive_status, set_archive_status] = useState(false);
	const [existing_files, set_existing_files] = useState();
	const [new_files, set_new_files] = useState();
	const [new_file_descriptions, set_new_file_descriptions] = useState({});
	const [new_drive_links, set_new_drive_links] = useState(['']);
	const [new_drive_link_ids, set_new_drive_link_ids] = useState([generateUniqueId()]);
	const [new_drive_link_descriptions, set_new_drive_link_descriptions] = useState({});
	// const [deleted_file_ids, set_deleted_file_ids] = useState();
	const [new_comment, set_new_comment] = useState('');
	const [bo_ticket_comments, set_bo_ticket_comments] = useState([]);
	const [active_key, set_active_key] = useState("1");
	const [selected_ticket, set_selected_ticket] = useState();
	const [view_mode, set_view_mode] = useState(true);
	const [show_back_office_ticket_drawer, set_show_back_office_ticket_drawer] = useState(false);
	const [selected_back_office_ticket, set_selected_back_office_ticket] = useState(null);

    const [jira_sprint, set_jira_sprint] = useState();
    const [jira_assignee, set_jira_assignee] = useState();
    const [jira_type, set_jira_type] = useState();
    const [jira_project, set_jira_project] = useState();
	const [jira_priority, set_jira_priority] = useState();
	const [jira_summary, set_jira_summary] = useState();
	const [jira_description, set_jira_description] = useState();
	const [show_jira_creation_flow, set_show_jira_creation_flow] = useState(false);
	const [show_jira_linking_flow, set_show_jira_linking_flow] = useState(false);
	const [jira_details, set_jira_details] = useState();
	const [jira_key, set_jira_key] = useState();
    const [jira_sprint_options, set_jira_sprint_options] = useState();
    const [checklist, set_checklist] = useState({});
    
    // Comment modal state
    const [show_comment_modal, set_show_comment_modal] = useState(false);
    const [pending_comment, set_pending_comment] = useState('');

    // File description editing state
    const [editing_file_id, set_editing_file_id] = useState(null);
    const [edit_description, set_edit_description] = useState('');

	const update_status = async(customer_ticket_id, status) => {
		try{
			let resp = await general_fetch({url:'customer_ticket/update', body:{customer_ticket_id, status}})
			fetch_customer_tickets()
			toastr.success('Successfully updated product ticket status')
		}catch(err){
			toastr.error(`Couldn't update product ticket status`)
			console.error(err)
		}
	}

    const follow_ticket = async ({customer_ticket_id}) => {
		try{
			let resp = await general_fetch({url:'customer_ticket/follow_ticket', body:{customer_ticket_id, follower_id: user_details.id}});
			toastr.success('Successfully followed product ticket')
			fetch_customer_tickets()
		}catch(err){
			console.error(err)
			toastr.error(`Couldn't follow product ticket`)
		}
    }

	// Helper function to construct FormData for ticket updates
	const buildTicketUpdateFormData = (commentOverride = null) => {
		const fd = new FormData();

		if (title != selected_ticket.title) fd.append('title', title);
		if (impact_area != selected_ticket.impact_area) fd.append('impact_area', impact_area);
		// if (org_id != selected_ticket.org_id) fd.append('org_id', org_id);
		if(org_ids) {
			fd.append('org_info', JSON.stringify(org_ids.map(x => {return {id: x.value}})))
		}
		if (priority != selected_ticket.priority) fd.append('priority', priority);
		if (type != selected_ticket.type) fd.append('type', type)
		if (expected_resolution_time != selected_ticket.expected_resolution_time) fd.append('expected_resolution_time', expected_resolution_time)
		if (assignee_id != selected_ticket.assignee_id) fd.append('product_manager_id', assignee_id)
		if (creator_id != selected_ticket.creator_id) fd.append('creator_id', creator_id)
		// if (customer_priority != selected_ticket.customer_priority) fd.append('customer_priority', customer_priority);
		if (description != selected_ticket.description) fd.append('description', description);
		if ((client_notification && !selected_ticket.client_notification) || (!client_notification && selected_ticket.client_notification)) fd.append('client_notification', client_notification)
		if ((auto_close && !selected_ticket.auto_close) || (!auto_close && selected_ticket.auto_close)) fd.append('auto_close', auto_close)
		if (design_link && design_link.split('/') && design_link.split('/').length > 4 && design_link.split('/')[4] != selected_ticket.design_branch_id) {
			fd.append('design_branch_id', design_link.split('/')[4])
		}
		if (status != selected_ticket.status) fd.append('status', status)

		// Add comment if there's a comment override (takes priority) or a pending comment
		const commentToUse = commentOverride !== null ? commentOverride : pending_comment;
		if (commentToUse) {
			fd.append('comment', commentToUse);
		}

		// Always send the archive status value to the server
		// This ensures that regardless of whether it changed or not, we're explicit about the value
		// Both the server and client need a consistent representation
		const currentArchivedStatus = !!selected_ticket.archived;
		const newArchivedStatus = !!archive_status;

		// Use 'archived' instead of 'archive_status' as that's what the server expects
		fd.append('archived', newArchivedStatus);
		fd.append('customer_ticket_id', selected_ticket.id);

		return fd;
	};

	// Shared function for making the ticket update API call
	const executeTicketUpdate = async (formData) => {
		try {
			set_page_loader(true, 'Please wait...');
			const resp = await general_fetch({ 
				url: 'customer_ticket/update', 
				body: formData, 
				is_form_data: true 
			});
			set_page_loader(false);
			fetch_customer_tickets();
			// Refresh the current ticket details without closing the drawer
			get_customer_ticket_by_id(selected_ticket.id);
			// Clear the pending comment after successful submission
			set_pending_comment('');
			toastr.success('Successfully updated Product Ticket');
			return resp;
		} catch (err) {
			set_page_loader(false);
			console.error(err);
			toastr.error(`Couldn't update product ticket details`);
			throw err;
		}
	};

	const update_ticket_details = async () => {
		try {
			// Check if status is being changed to 'sent_back_by_lead' and no pending comment
			if (status != selected_ticket.status && status === 'sent_back_by_lead' && !pending_comment) {
				set_show_comment_modal(true);
				return;
			}

			const fd = buildTicketUpdateFormData();
			await executeTicketUpdate(fd);

		} catch (err) {
			// Error handling is done in executeTicketUpdate
		}
	}

	const handle_comment_submit = async (comment) => {
		try {
			set_pending_comment(comment);
			set_show_comment_modal(false);

			// Pass the comment directly to avoid race condition with state update
			const fd = buildTicketUpdateFormData(comment);
			await executeTicketUpdate(fd);

		} catch (err) {
			// Error handling is done in executeTicketUpdate
		}
	};

	const add_ticket_files = async () => {
		try {
			// Validate that all new files have descriptions
			const missingDescriptions = [];
			const tooLongDescriptions = [];

			if (new_files && new_files.length) {
				new_files.forEach((file, idx) => {
					const description = (new_file_descriptions[file.uid] || '').trim();
					if (!description) {
						missingDescriptions.push(file.name);
					} else if (description.length > MAX_DESCRIPTION_LENGTH) {
						tooLongDescriptions.push(file.name);
					}
				});
			}

			if (missingDescriptions.length > 0) {
				const fileList = missingDescriptions.map(name => `• ${name}`).join('; ');
				toastr.error(`File description is mandatory. Please add descriptions for: ${fileList}`, {
					timeOut: 10000
				});
				return;
			}

			if (tooLongDescriptions.length > 0) {
				const fileList = tooLongDescriptions.map(name => `• ${name}`).join('; ');
				toastr.error(`File description too long (max ${MAX_DESCRIPTION_LENGTH} characters): ${fileList}`, {
					timeOut: 10000
				});
				return;
			}

			// Check if any existing files lack descriptions
			const existingFilesWithoutDesc = [];
			if (existing_files && existing_files.length) {
				existing_files.forEach((file, idx) => {
					if (!file.file_description || file.file_description.trim() === '') {
						const fileName = file.global_path ? file.file_path : `file_${idx + 1}`;
						existingFilesWithoutDesc.push({id: file.id, name: fileName});
					}
				});
			}

			if (existingFilesWithoutDesc.length > 0) {
				const fileList = existingFilesWithoutDesc.map(f => `• ${f.name}`).join('; ');
				toastr.error(`Before adding new files, please add descriptions to all existing files: ${fileList}. Click the edit icon next to each file.`, {
					timeOut: 10000
				});
				return;
			}

			let fd = new FormData();

			if (new_files && new_files.length) {
				// Create an array to store file descriptions in the order of files
				const descriptionsArray = [];
				for (let file of new_files) {
					fd.append('files', file.originFileObj)
					// Add description for this file
					const description = (new_file_descriptions[file.uid] || '').trim();
					descriptionsArray.push(description);
				}
				fd.append('file_descriptions', JSON.stringify(descriptionsArray));
			}
			fd.append('customer_ticket_id', selected_ticket.id)
			set_page_loader(true, 'Please wait...')
			let resp = await general_fetch({ url: 'customer_ticket/update', body: fd, is_form_data: true })
			set_page_loader(false)
			get_customer_ticket_by_id(selected_ticket_id)
			set_new_files([])
			set_new_file_descriptions({})
			toastr.success('Successfully added Files')

		} catch (err) {
			set_page_loader(false)
			console.error(err)
			toastr.error(`Couldn't add Files`)
		}
	}

	const getExistingFilesWithoutDesc = (files) => {
		const existingFilesWithoutDesc = [];
		if (files && files.length) {
			files.forEach((file, idx) => {
				if (!file.file_description || file.file_description.trim() === '') {
					const fileName = file.global_path ? file.file_path : `file_${idx + 1}`;
					existingFilesWithoutDesc.push({ id: file.id, name: fileName });
				}
			});
		}
		return existingFilesWithoutDesc;
	};

	const showMissingFileDescriptionsError = (existingFilesWithoutDesc) => {
		if (!existingFilesWithoutDesc || !existingFilesWithoutDesc.length) {
			return false;
		}

		const fileList = existingFilesWithoutDesc
			.map(f => `• ${f.name}`)
			.join('; ');

		toastr.error(
			`Before adding new links, please add descriptions to all existing files/links: ${fileList}. Click the edit icon next to each file.`,
			{
				timeOut: 10000
			}
		);

		return true;
	};

	const add_drive_links = async () => {
		try {
			// Check if any existing files/links lack descriptions before adding new links
			const existingFilesWithoutDesc = getExistingFilesWithoutDesc(existing_files);

			if (showMissingFileDescriptionsError(existingFilesWithoutDesc)) {
				return;
			}

			// Validate that all new drive links have descriptions
			const missingLinkDescriptions = [];
			const tooLongLinkDescriptions = [];

			new_drive_links.forEach((link, idx) => {
				const trimmedLink = link ? link.trim() : '';
				if (trimmedLink !== '') {
					const description = (new_drive_link_descriptions[idx] || '').trim();
					const linkPreview = `Link ${idx + 1}: ${trimmedLink.substring(0, 50)}${trimmedLink.length > 50 ? '...' : ''}`;

					if (!description) {
						missingLinkDescriptions.push(linkPreview);
					} else if (description.length > MAX_DESCRIPTION_LENGTH) {
						tooLongLinkDescriptions.push(linkPreview);
					}
				}
			});

			if (missingLinkDescriptions.length > 0) {
				const linkList = missingLinkDescriptions.map(name => `• ${name}`).join('; ');
				toastr.error(`Link description is mandatory. Please add descriptions for: ${linkList}`, {
					timeOut: 10000
				});
				return;
			}

			if (tooLongLinkDescriptions.length > 0) {
				const linkList = tooLongLinkDescriptions.map(name => `• ${name}`).join('; ');
				toastr.error(`Link description too long (max ${MAX_DESCRIPTION_LENGTH} characters): ${linkList}`, {
					timeOut: 10000
				});
				return;
			}

			let body = {}

			if(new_drive_links && new_drive_links.length){
				// Filter out empty and whitespace-only links
				const temp_drive_links = new_drive_links
					.map(link => link ? link.trim() : '')
					.filter(link => link !== '');

				if(temp_drive_links && temp_drive_links.length){
					body['drive_links'] = temp_drive_links

					// Send drive link descriptions as a JSON array (matching the order of non-empty links)
					const linkDescriptionsArray = [];
					new_drive_links.forEach((link, idx) => {
						const trimmedLink = link ? link.trim() : '';
						if (trimmedLink !== '') {
							const description = (new_drive_link_descriptions[idx] || '').trim();
							linkDescriptionsArray.push(description);
						}
					});
					body['drive_link_descriptions'] = JSON.stringify(linkDescriptionsArray);
				}else{
					toastr.error('Link cannot be empty')
					return
				}
			}
			body['customer_ticket_id'] = selected_ticket_id
			set_page_loader(true, 'Please wait...')
			let resp = await general_fetch({ url: 'customer_ticket/update', body })
			set_page_loader(false)
			get_customer_ticket_by_id(selected_ticket_id)
			set_new_drive_links([''])
			set_new_drive_link_ids([generateUniqueId()])
			set_new_drive_link_descriptions({})
			toastr.success('Successfully added Links')

		} catch (err) {
			set_page_loader(false)
			console.error(err)
			toastr.error(`Couldn't add Links`)
		}
	}

	const update_file_description = async (file_id, description) => {
		try {
			// Validate that description is not empty
			const trimmedDescription = description ? description.trim() : '';

			if (!trimmedDescription) {
				toastr.error('File description cannot be empty', {
					timeOut: 5000
				});
				return;
			}

			if (trimmedDescription.length > MAX_DESCRIPTION_LENGTH) {
				toastr.error(`File description too long (max ${MAX_DESCRIPTION_LENGTH} characters)`, {
					timeOut: 5000
				});
				return;
			}

			set_page_loader(true, 'Please wait...')
			await general_fetch({
				url: 'customer_ticket/update',
				body: {
					customer_ticket_id: selected_ticket.id,
					file_id: file_id,
					file_description: trimmedDescription
				}
			})
			set_page_loader(false)
			get_customer_ticket_by_id(selected_ticket_id)
			set_editing_file_id(null)
			set_edit_description('')
			toastr.success('Successfully updated file description')
		} catch (err) {
			set_page_loader(false)
			set_editing_file_id(null)
			set_edit_description('')
			console.error(err)
			toastr.error(`Couldn't update file description`)
		}
	}

	const get_comments = async (customer_ticket_id) => {
		try {
			let resp = await general_fetch({ 'url': 'customer_ticket/get_comments', body: { customer_ticket_id: customer_ticket_id ? customer_ticket_id : selected_ticket.id } })
			set_bo_ticket_comments(resp)
		} catch (err) {
			console.error(err)
			toastr.error(`Couldn't get comments`)
		}
	}

	const add_comment = async () => {
		try {
			let resp = await general_fetch({ 'url': 'customer_ticket/add_comment', body: { customer_ticket_id: selected_ticket.id, comment: new_comment } })
			get_comments()
			set_new_comment('')
		} catch (err) {
			console.error(err)
			toastr.error(`Couldn't add comment`)
		}
	}
	const create_jira = async () => {
		try {
			set_page_loader(true, 'Please wait...')
			// Convert Google Space thread link to full URL if it exists
			const google_space_thread_link = selected_ticket && selected_ticket.cs_team_doubts_thread
				? convertGoogleSpaceThreadToUrl(selected_ticket.cs_team_doubts_thread)
				: null;
			await general_fetch({ 'url': 'customer_ticket/create_jira_ticket', body: { customer_ticket_id: selected_ticket.id, summary: jira_summary, description: jira_description, project: jira_project.value, type: jira_type.value, priority: jira_priority.value, assignee: jira_assignee.value, jira_sprint: jira_sprint.value, google_space_thread_link: google_space_thread_link } })
			set_page_loader(false)
			fetch_customer_tickets()
			set_show_jira_creation_flow(false)
			set_show_jira_linking_flow(false)
            set_jira_assignee('');
			set_jira_priority('');
            set_jira_sprint();
            set_jira_type('')
            set_jira_project('')
			set_jira_summary('')
			set_jira_description('')
			get_customer_ticket_by_id(selected_ticket_id)
			let jira_details = await get_jira_details()
			toastr.success(`Successfully created Jira issue`)
            let ticket_status = 'jira_added';
            if(jira_details) {
                if(jira_details.status == 'Done') {
                    ticket_status = 'jira_done';
                } else if(['In Progress', 'Dev', 'Stage', 'Preprod'].includes(jira_details.status)) {
                    ticket_status = 'jira_in_progress';
                }
            }
            update_status(selected_ticket.id, ticket_status)
            get_customer_ticket_by_id(selected_ticket.id)
		} catch (err) {
			set_page_loader(false)
			console.error(err)
			toastr.error(`Couldn't create Jira issue`)
		}
	}

	const link_existing_jira = async () => {
		try {
			let jiraKeyInput = jira_key && jira_key.trim();
			let extractedJiraKey = jiraKeyInput;
			// Accept both CT-1234 and full Jira URL
			if (jiraKeyInput && jiraKeyInput.startsWith('http')) {
				const match = jiraKeyInput.match(/\/browse\/([A-Z]+-\d+)/i);
				if (match && match[1]) {
					extractedJiraKey = match[1];
				}
			}
			if(!extractedJiraKey || extractedJiraKey.includes('infurniacoretech.atlassian.net')){
				toastr.error(`Please provide a jira issue key`)
				return
			}

			// Convert Google Space thread link to full URL if it exists
			const google_space_thread_link = selected_ticket && selected_ticket.cs_team_doubts_thread
				? convertGoogleSpaceThreadToUrl(selected_ticket.cs_team_doubts_thread)
				: null;
			await general_fetch({url:'customer_ticket/link_existing_jira', body:{customer_ticket_id: selected_ticket.id, jira_key: extractedJiraKey.toUpperCase(), google_space_thread_link: google_space_thread_link}})
			fetch_customer_tickets()
			set_jira_key('')
			set_show_jira_creation_flow(false)
			set_show_jira_linking_flow(false)
			set_jira_details('')
            let jira_details = await get_jira_details();
			toastr.success(`Successfully linked Jira issue`)
            let ticket_status = 'jira_added';
            if(jira_details) {
                if(jira_details.status == 'Done') {
                    ticket_status = 'jira_done';
                } else if(['In Progress', 'Dev', 'Stage', 'Preprod'].includes(jira_details.status)) {
                    ticket_status = 'jira_in_progress';
                }
            }
            update_status(selected_ticket.id, ticket_status)
            get_customer_ticket_by_id(selected_ticket.id)
		} catch (err) {
			set_page_loader(false)
			console.error(err)
			toastr.error(`Couldn't link Jira issue`)
		}
	}

	const get_jira_details = async (customer_ticket_id) => {
		try {
			let resp = await general_fetch({ 'url': 'customer_ticket/get_jira_details', body: { customer_ticket_id: customer_ticket_id ? customer_ticket_id : selected_ticket.id } })
			if(resp){
				set_jira_details(resp)
			}
            return resp;
		} catch (err) {
			console.error(err)
		}
	}

	const get_assignee_list = () => {
		if(cs_org_users && cs_org_users.length){
			let temp_cs_org_users = JSON.parse(JSON.stringify(cs_org_users))
            temp_cs_org_users = temp_cs_org_users.filter(o => o.role.includes('infurnia_product_manager'))
			return temp_cs_org_users.map(o => { return { value: o.id, label: o.first_name } }) 
		}
		return []
	}

	const delink_jira_issue = async() => {
		try {
			let resp = await general_fetch({'url':'customer_ticket/delink_jira_issue',body:{customer_ticket_id: selected_ticket_id}})
			fetch_customer_tickets()
			set_jira_key('')
			set_show_jira_creation_flow(false)
			set_show_jira_linking_flow(false)
			get_customer_ticket_by_id(selected_ticket_id)
			toastr.success(`Successfully delinked Jira issue`)
		}catch (err) {
			console.error(err)
			toastr.error(`Couldn't delinked Jira issue`)
		}
	}

	const get_customer_ticket_by_id = async (customer_ticket_id) => {
		try {
			let resp = await general_fetch({'url':'customer_ticket/get_by_id',body:{customer_ticket_id}})
			set_selected_ticket(resp)
            return resp;
		}catch (err) {
			console.error(err)
		}
	}

	const delete_file = async (file_id) => {
		try {
			set_page_loader(true, 'Please wait...')
			let resp = await general_fetch({'url':'customer_ticket/delete_file',body:{customer_ticket_id: selected_ticket_id, file_id}})
			set_page_loader(false)
			get_customer_ticket_by_id(selected_ticket_id)
		}catch (err) {
			console.error(err)
		}
	}

    const update_jira_due_date = async (jira_id, due_date) => {
		try {
			let resp = await general_fetch({'url':'customer_ticket/update_jira_due_date', body:{jira_id, due_date}})
            get_jira_details()
			toastr.success(`Successfully updated jira due date`)
		}catch (err) {
			console.error(err)
			toastr.error(`Couldn't update Jira due date`)
		}
    }

	const select_back_office_ticket = back_office_ticket_id => {
		set_selected_back_office_ticket(back_office_ticket_id)
		set_show_back_office_ticket_drawer(true)
	}

    const get_jira_link = (jira_key) => {
        return `https://infurniacoretech.atlassian.net/browse/${jira_key}`;
    }

    const update_checklist_status = async (key) => {
        let c = JSON.parse(JSON.stringify(checklist))
        c[key] = c[key] ? false : true;
        try {
			let resp = await general_fetch({url:'customer_ticket/update', body:{customer_ticket_id: selected_ticket.id, checklist: JSON.stringify(c)}})
            set_checklist((prev) => {
                let new_checklist = JSON.parse(JSON.stringify(prev))
                new_checklist[key] = new_checklist[key] ? false : true;
                return new_checklist
            })
        } catch(err) {
            toastr.error('Could not update checklist status')
        }
    }

	const onclick_handleclose = () => {
		// Check for unsaved file description edits
		if (editing_file_id !== null) {
			const confirmClose = window.confirm('You have unsaved changes to a file description. Are you sure you want to close? All unsaved changes will be lost.');
			if (!confirmClose) {
				return;
			}
		}

		// Check for unsaved new files/links
		if ((new_files && new_files.length > 0) ||
		    (new_drive_links && new_drive_links.some(link => link && link.trim() !== ''))) {
			const confirmClose = window.confirm('You have new files or links that have not been submitted. Are you sure you want to close? All data will be lost.');
			if (!confirmClose) {
				return;
			}
		}

		toggle()
		set_title('')
        set_impact_area()
		set_type('')
		// set_org_id('')
        set_org_ids([])
        set_org_ids_options([])
		set_priority('')
		set_assignee_id('')
        set_creator_id('')
		// set_customer_priority('')
		set_client_notification(true)
		set_auto_close(true)
		set_description('')
		set_design_link('')
		set_status('')
		set_archive_status(false)
		set_new_files([])
		set_existing_files([])
		set_new_file_descriptions({})
		set_new_drive_links([''])
		set_new_drive_link_ids([generateUniqueId()])
		set_new_drive_link_descriptions({})
		// set_deleted_file_ids([])
		set_bo_ticket_comments([])
		set_active_key("1")
		set_show_jira_creation_flow(false)
		set_show_jira_linking_flow(false)
        set_jira_assignee('')
		set_jira_priority('')
        set_jira_sprint()
        set_jira_type('')
        set_jira_project('')
		set_jira_summary('')
		set_jira_description('')
		set_jira_details('')
        set_checklist({})
		set_view_mode(false)
		set_editing_file_id(null)
		set_edit_description('')
	}

	useEffect(() => {
		if (selected_ticket) {
			set_title(selected_ticket.title);
            set_checklist(JSON.parse(selected_ticket.checklist))
            set_impact_area(selected_ticket.impact_area);
			set_type(selected_ticket.type);
            if(selected_ticket.type == 'feature') {
                set_jira_type(jira_type_options[1])
            } else if(selected_ticket.type == 'bug') {
                set_jira_type(jira_type_options[2])
            }
			set_expected_resolution_time(selected_ticket.expected_resolution_time)
			set_assignee_id(selected_ticket.product_manager_id);
            set_creator_id(selected_ticket.creator_id);
            if(all_orgs && all_orgs.length > 0) {
                let org_map = {};
                for(let org of all_orgs) { org_map[org.id] = org; }
                let parsed_org_ids = parse_org_info(selected_ticket.org_info);
                let current_org_ids = [], this_org_ids_options = [], option_map = {};
                for(let org_id of parsed_org_ids) {
                    let org = org_map[org_id];
                    if(org) {
                        this_org_ids_options.push({value: org.id, label: org.name, editable: false});
                        current_org_ids.push({value: org.id, label: org.name, editable: false});
                        option_map[org.id] = this_org_ids_options.length - 1;
                    }
                }
                if(check_all_org_setting_access({user_details, selected_ticket, team_info})) {
                    for(let org of all_orgs) {
                        if(option_map[org.id] != undefined) {
                            this_org_ids_options[option_map[org.id]].editable = true;
                            current_org_ids[option_map[org.id]].editable = true;
                        } else {
                            this_org_ids_options.push({value: org.id, label: org.name, editable: true});
                        }
                    }
                } else {
                    let all_editable_store_ids = ['1504672400687'];
                    for(let org of all_orgs) {
                        if(org.customer_success_executive_id == user_details.id || all_editable_store_ids.includes(org.id)) {
                            if(option_map[org.id] != undefined) {
                                this_org_ids_options[option_map[org.id]].editable = true;
                                current_org_ids[option_map[org.id]].editable = true;
                            } else {
                                this_org_ids_options.push({value: org.id, label: org.name, editable: true});
                            }
                        }
                    }
                }
                set_org_ids_options(this_org_ids_options);
                set_org_ids(order_org_id_options(current_org_ids));
            }
			set_priority(selected_ticket.priority);
			// set_customer_priority(selected_ticket.customer_priority);
			set_description(selected_ticket.description);
			set_status(selected_ticket.status);
			set_client_notification(selected_ticket.client_notification);
			set_auto_close(selected_ticket.auto_close);
			// Convert archived status to a definite boolean (true/false)
			// Use double-negation to ensure we have a proper boolean value
			// The property name from the server is 'archived', not 'archive_status'
			set_archive_status(!!selected_ticket.archived);
			if(selected_ticket.design_branch_id)set_design_link('https://design.infurnia.com/design/' + selected_ticket.design_branch_id)
			set_existing_files(selected_ticket.files)
            get_comments(selected_ticket.id)
		}
	}, [selected_ticket, all_orgs]);

	useEffect(() => {
		if(open && selected_ticket_id) get_customer_ticket_by_id(selected_ticket_id)
	}, [open, selected_ticket_id]);

	useEffect(() => {
		if(set_view_mode_true) {
            set_view_mode(true)
            return
        }
        let edit_access = check_ticket_edit_access({user_details, selected_ticket, team_info});
        if(edit_access) {
            set_view_mode(false)
        } else {
            set_view_mode(true)
        }
	}, [set_view_mode_true, user_details, selected_ticket, team_info]);

	const AssignedTo = () => (
		<div className="mb-4" style={{ position: 'relative', zIndex: '5' }}>
			<div className='mb-2 bold'>Product Manager</div>
			<Select
				// closeMenuOnSelect={false}
				// components={animatedComponents}
				value={{ value: assignee_id, label: cs_org_users && cs_org_users.length && assignee_id ? cs_org_users.find(o => o.id === assignee_id).first_name : '' }}
				// isMulti
				options={get_assignee_list()}
				styles={colourStyles4}
				onChange={(selectedOptions) => set_assignee_id(selectedOptions.value)}
				isDisabled={view_mode}
			/>
		</div>
	)

	const Owner = () => (
		<div className="mb-4" style={{ position: 'relative', zIndex: '4' }}>
			<div className='mb-2 bold'>Owner</div>
			<Select
				// closeMenuOnSelect={false}
				// components={animatedComponents}
				value={{ value: creator_id, label: cs_org_users && cs_org_users.length && creator_id ? cs_org_users.find(o => o.id === creator_id).first_name : '' }}
				// isMulti
				options={cs_org_users ? cs_org_users.map(o => { return { value: o.id, label: o.first_name } }) : []}
				styles={colourStyles4}
				onChange={(selectedOptions) => set_creator_id(selectedOptions.value)}
				isDisabled={view_mode}
			/>
		</div>
	)

	const CommentsTab = () => (
		<>						<div className="mb-3 bold">Comments <Tooltip title='Refresh comments'><AntdButton className="ml-1 px-2 py-1"><span className="cp" onClick={() => get_comments()} style={{ opacity: '0.5' }}><i className="fa fa-refresh" /></span></AntdButton></Tooltip></div>
			<div className="mb-4" style={{ display: 'flex' }}>
				<div className="avatar_div mr-2">{user_details && user_details.first_name ? user_details.first_name[0] : ':)'}</div>
				<div style={{ width: "100%" }}>
					<textarea value={new_comment} onChange={(e) => set_new_comment(e.target.value)} style={{ height: '100px', width: '100%', borderRadius: '6px', border: '1px solid #cad1d7' }} className='px-2 py-1' placeholder="Add a comment..." />
					{
						new_comment ?
							<div>
								<AntdButton onClick={() => set_new_comment('')} className="mr-2">Cancel</AntdButton>
								<AntdButton onClick={add_comment} type="primary">Save</AntdButton>
							</div> : ""
					}
				</div>
			</div>
		{
			bo_ticket_comments && bo_ticket_comments.length ? bo_ticket_comments.map((single_comment, idx) => (
				<div className="mb-4" style={{ display: 'flex' }}>
					<div className="avatar_div mr-2">{single_comment.commented_by && cs_org_users && cs_org_users.length ? cs_org_users.find(o => o.id === single_comment.commented_by).first_name[0] : ':)'}</div>
					<div style={{ width: "100%" }}>
						<div className="flex_property mb-2">
							<div className="mr-2" style={{ color: '#44546f', fontWeight: 500 }}>{single_comment.commented_by && cs_org_users && cs_org_users.length ? cs_org_users.find(o => o.id === single_comment.commented_by).first_name : 'Anonymous User'}</div>
							<div style={{ opacity: '0.5' }}>{timeSince(single_comment.updated_at)}</div>
						</div>
						<div>{single_comment.comment}</div>
						{user_details && single_comment.commented_by === user_details.id ?
							<div>
	
							</div> : ''
						}
					</div>
				</div>
			)):''
		}
		</>
	)

	const TicketDetails = () => (
		<>
            <Row>
                <Col md={8}>
                    <div className='mb-2 bold'>
                        Title*
                        {
                            (selected_ticket && selected_ticket.cs_team_doubts_thread ? <a style={{display: 'inline-block', marginLeft: '10px', verticalAlign: 'bottom'}} href={convertGoogleSpaceThreadToUrl(selected_ticket.cs_team_doubts_thread) || ''} target="_blank"><i className="fa fa-external-link"></i></a> : '')
                        }
                    </div>
                    <Input readOnly={view_mode} type='text' style={{ height: '36px', color: '#525f7f'  }} className='px-2 py-1 mb-4' placeholder={'Type'} value={title} onChange={(e) => set_title(e.target.value)} />
                </Col>
                    <Col md={4}>
                        <div className='mb-2 bold'>Impact Area*</div>
                        <div style={{ zIndex: 999999999, position: 'relative' }}>
                            <Select
                                value={{ value: impact_area, label: impact_area }}
                                options={impact_area_options}
                                onChange={(selectedOptions) => set_impact_area(selectedOptions.value)}
                                isDisabled={view_mode}
                            />
                        </div>
                    </Col>
            </Row>

			<div className='mb-2 bold'>Org</div>
			<div className="mb-4">
				{
					current_org ?
						<Input type='text' style={{ height: '36px' }} className='px-2 py-1' value={current_org.name} readOnly /> :
                        <Select
                            // closeMenuOnSelect={false}
                            // components={animatedComponents}
                            value={org_ids}
                            isMulti
                            styles={org_id_option_style}
                            options={org_ids_options}
                            isClearable={org_ids.some((v) => v.editable)}
                            onChange={(selectedOptions, actionMeta) => {
                                switch (actionMeta.action) {
                                    case 'remove-value':
                                    case 'pop-value':
                                        if (!actionMeta.removedValue.editable) {
                                            return;
                                        }
                                        break;
                                    case 'clear':
                                        selectedOptions = org_ids_options.filter((v) => !v.editable);
                                        break;
                                }
                                set_org_ids(order_org_id_options(selectedOptions))
                            }}
                        />
				}
			</div>
            {
                // <>
                //     <div className='mb-4 bold'>Customer priority or Client misery level (1 is highest priority, 5 is lowest)</div>
                //     <div className="flex_property mb-4" style={{ fontWeight: 400 }}>
                //         <div>
                //             <div style={{ minHeight: '32px' }}></div>
                //             {/* <div style={{ minHeight: '32px', fontSize: '12px' }}>High priority for customer</div> */}
                //         </div>
                //         {[1, 2, 3, 4, 5].map(x => (
                //             <div>
                //                 <div style={{ minHeight: '32px' }} className="ml-2 mr-2">{x}</div>
                //                 <div style={{ minHeight: '32px' }} className="ml-2 mr-2" onClick={() => set_customer_priority(x)} >
                //                     <input disabled={view_mode} type="radio" checked={customer_priority === x ? true : false} />
                //                 </div>
                //             </div>
                //         ))}
                //         <div>
                //             <div style={{ minHeight: '32px' }}></div>
                //             {/* <div style={{ minHeight: '32px', fontSize: '12px' }}>Low priority for customer</div> */}
                //         </div>
                //     </div>
                // </>
            }

			<div className='mb-2 bold'>Description</div>
			<textarea readOnly={view_mode} style={{ height: '100px', width: '100%', borderRadius: '6px', border: '1px solid #cad1d7', color: '#525f7f' }} className='px-2 py-1 mb-4' value={description} onChange={(e) => set_description(e.target.value)} placeholder='e.g. Cutlist and preferences set up' />
			{
				// selected_ticket && selected_ticket.back_office_ticket && selected_ticket.back_office_ticket.type === 'bug'?
				// <>
				// 	<div className='mb-2 bold'>Design Link</div>
				// 	<div className="flex_property mb-4" style={{ width: '100%' }}>
				// 		<Input readOnly={view_mode} type='text' style={{ height: '36px', width: '60%' }} className='px-2 py-1 mr-2' placeholder={'Type...'} value={design_link} onChange={(e) => set_design_link(e.target.value)} />
				// 		{/* {org_id && !view_mode ? <Button disabled={view_mode} color='secondary'>Add "pm1" to the Org</Button> : ''} */}
				// 	</div>
				// </>:""
			}
            <Row>
                <Col>
                    <label className="form-control-label mb-4 flex_property" htmlFor="input-address" style={{ minWidth: '60%', marginBottom: 0 }}>
                        <div className='mr-2'>Type</div>
                        <div style={{ minHeight: '32px', fontWeight: 400 }} className="ml-2 mr-2 flex_property" onClick={() => { set_type('feature') }} >
                            <input disabled={view_mode} type="radio" className="mr-2" checked={type === 'feature' ? true : false} />
                            <div className="mr-4">Feature</div>
                        </div>
                        <div style={{ minHeight: '32px', fontWeight: 400 }} className="ml-2 mr-2 flex_property" onClick={() => { set_type('bug') }} >
                            <input disabled={view_mode} type="radio" className="mr-2" checked={type === 'bug' ? true : false} />
                            <div>Bug</div>
                        </div>
                    </label>
                </Col>
                <Col>
                        <div className="flex_property mr-1 mt-1 mb-4" style={{flexBasis: '15%', opacity:'0.5'}}>
                            Created - {selected_ticket ? date_string(selected_ticket.created_at) : ''}
                        </div>
                </Col>
            </Row>
            {
                // <>
                //     <div className='mb-2 bold'>Priority. 1 is highest priority, 5 is lowest</div>
                //     <div className="flex_property mb-4" style={{ fontWeight: 400 }}>
                //         {[1, 2, 3, 4, 5].map(x => (
                //             <div>
                //                 <div style={{ minHeight: '32px' }} className="ml-2 mr-2">{x}</div>
                //                 <div style={{ minHeight: '32px' }} className="ml-2 mr-2" onClick={() => set_priority(x)} >
                //                     <input disabled={view_mode} type="radio" checked={priority === x ? true : false} />
                //                 </div>
                //             </div>
                //         ))}
                //     </div>
                // </>
            }
            {
                /* (view_mode || user_details && user_details.role && user_details.role.store && (user_details.role.store.includes('infurnia_product_manager') || user_details.role.store.includes('customer_success_admin'))) ?
                    AssignedTo() : '' */
            }
            {
                <Row>
                    <Col md={6}>
                        {
                            (view_mode || user_details && user_details.role && user_details.role.store && (user_details.role.store.includes('infurnia_product_manager') || user_details.role.store.includes('customer_success_admin'))) ?
                                AssignedTo() : ''
                        }
                    </Col>
                        <Col md={6}>
                        {
                            Owner()
                        }
                        </Col>
                </Row>

            }
            {
                <div className="mb-4">
                    <div className='mb-2 bold'>Priority ({MAX_PRIORITY} fire is highest priority)</div>
                    <Rate 
                        className='priority_rating' 
                        disabled={!check_ticket_edit_access({user_details, selected_ticket, team_info})} 
                        onChange={(value) => set_priority(value ? value : 0)} 
                        value={priority ? priority : 0} 
                        character={({index = 0}) => <FireFilled/>}
                        count={MAX_PRIORITY}
                    />
                </div>
            }
			<div className='mb-2 bold'>Status</div>
			<div className="flex_property mb-4" style={{ width: '100%' }}>
				<div style={{ flex: 3, position: 'relative', zIndex: 3 }}>
					<Select
						// closeMenuOnSelect={false}
						// components={animatedComponents}
						value={{ value:status, label:customer_tickets_status_options.find(o => o.value===status)?customer_tickets_status_options.find(o => o.value===status).label:'' }}
						// isMulti
                        menuPortalTarget={document.body}
                        menuPlacement="bottom"
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999998 }),
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                            control: (provided) => ({
                                ...provided,
                                zIndex: 3
                            })
                        }}
						options={customer_tickets_status_options}
						onChange={(selectedOptions) => {set_status(selectedOptions.value)}}
						isDisabled={view_mode}
					/>
				</div>
				{
					selected_ticket && selected_ticket.customer_id?
					<div className="ml-4 flex_property" style={{ flex: 2 }}>
						<div className="mr-1">Client Notification</div> <Switch disabled={view_mode} size="small" checked={client_notification} onChange={() => set_client_notification(!client_notification)} />
					</div>:''
				}
			</div>
            
            <div className='mb-2 bold'>Archival Status</div>
			<div className="mb-4" style={{ width: '100%', position: 'relative', zIndex: 2 }}>
                <Select
                    // Use the boolean value directly for option matching
                    value={archive_status ? 
                        { value: true, label: "Archived" } : 
                        { value: false, label: "Unarchived" }
                    }
                    options={[
                        { value: true, label: "Archived" },
                        { value: false, label: "Unarchived" }
                    ]}
                    menuPortalTarget={document.body}
                    menuPlacement="bottom"
                    styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999999 }),
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                        // Add additional styling to make the dropdown more visible
                        control: (provided) => ({
                            ...provided,
                            backgroundColor: 'white'
                        }),
                        option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected ? '#2684FF' : 'white',
                            color: state.isSelected ? 'white' : 'black'
                        })
                    }}
                    onChange={(selectedOption) => {
                        const newValue = selectedOption.value === true;
                        set_archive_status(newValue);
                    }}
                    isDisabled={view_mode}
                />
			</div>
            {
                /* (view_mode || user_details && user_details.role && user_details.role.store && (user_details.role.store.includes('infurnia_product_manager') || user_details.role.store.includes('customer_success_admin'))) ?
                    <>
                        <div className='mb-2 bold'>Expected Resolution Time (Days)</div>
                        <div className="flex_property mb-4" style={{ width: '60%', }}>
                            <Input type='number' readOnly={view_mode} value={expected_resolution_time} onChange={(e) => set_expected_resolution_time(e.target.value)} />
                        </div>
                    </>: '' */
            }
			{<Button color="primary" onClick={update_ticket_details}>Update Product Ticket Details</Button>}
		</>
	)
    const open_jira_creation_flow = async () => {

        /*set_page_loader(true, 'Loading sprints...')
        let res = await general_fetch({url: 'customer_ticket/get_jira_sprints'});
        set_jira_sprint_options((res ? res : []).map(x => { return {value: x, label: x.name }}));
        set_show_jira_creation_flow(true)
        set_page_loader(false)*/
	
		set_page_loader(true, 'Loading sprints...');

		// 1. Pre-populate summary with title
		set_jira_summary(title || '');

		// 2. Build description
		let desc = "Issue\n";
		desc += description ? description + '\n\n' : '';

		// Append design link if present
		if (design_link) {
			desc += `Design Link: ${design_link}\n`;
		}

		// Append file URLs (uploaded files)
		if (existing_files && existing_files.length) {
			const fileUrls = existing_files
				.filter(file => !file.global_path)
				.map((file, idx) => `File ${idx + 1}: https://storage.googleapis.com/${global.config.ticketing_bucket_name}${file.file_path}`);
			if (fileUrls.length) {
				desc += `Files:\n${fileUrls.join('\n')}\n`;
			}
			// Append drive links (global_path)
			const driveLinks = existing_files
				.filter(file => file.global_path)
				.map((file, idx) => `Link ${idx + 1}: ${file.file_path}`);
			if (driveLinks.length) {
				desc += `Links:\n${driveLinks.join('\n')}\n`;
			}
		}

		// Append org names
		if (current_org) {
			desc += `Org: ${current_org.name}\n`;
		} else if (org_ids && org_ids.length && org_ids_options && org_ids_options.length) {
			const orgNames = org_ids
				.map(org => {
					const found = org_ids_options.find(opt => opt.value === org.value);
					return found ? found.label : '';
				})
				.filter(Boolean);
			if (orgNames.length) {
				desc += `Orgs: ${orgNames.join(', ')}\n`;
			}
		}

		desc+= '\n\nActionable\n\n'

		set_jira_description(desc);

		// 3. Load sprints and show flowf
		let res = await general_fetch({url: 'customer_ticket/get_jira_sprints'});
		set_jira_sprint_options((res ? res : []).map(x => { return {value: x, label: x.name }}));
		set_show_jira_creation_flow(true);
		set_page_loader(false);
	}

	const JiraTab = () => (
			(
				selected_ticket && selected_ticket.jira_key && !show_jira_creation_flow && !show_jira_linking_flow?
				<>
					<div className="flex_property mb-3">
						<div className="mr-2">Jira Issue Link:</div>
						<a href={get_jira_link(selected_ticket.jira_key)} target="_blank" className="mr-2 flex_property" style={{textDecoration:'underline', color:'#0078ff'}}><img src='/jira.svg' height={'18px'} width={'18px'} className="mr-1"/><div>{selected_ticket.jira_key}</div></a>
					</div>
					<div className="mb-3 flex_property">
						<div className="mr-2">Status:</div>
						<div className="bold">{jira_details ? jira_details.status : ''}</div>
					</div>
					<div className="mb-3 flex_property">
						<div className="mr-2">Sprint:</div>
						<div className="bold">{jira_details && jira_details.sprint && jira_details.sprint != '' ? jira_details.sprint : 'Backlog'}</div>
					</div>
					<div className="mb-3 flex_property">
						<div className="mr-2">Due Date:</div>
                        {
                            jira_details ? <DatePicker 
                                format="YYYY-MM-DD" 
                                disabledDate={disabledDate}
                                onChange={(date, dateString) => update_jira_due_date(jira_details.id, dateString)} 
                                defaultValue={jira_details.due_date ? dayjs(jira_details.due_date, 'YYYY-MM-DD') : null}
                            /> : ''
                        }
                        {
                            // <div className="bold">{jira_details ? jira_details.due_date : ''}</div>
                        }
					</div>
					<div className="mb-3 flex_property">
						<div className="mr-2">Fix Version:</div>
						<div className="bold">{jira_details ? jira_details.fix_version : ''}</div>
					</div>
					<div className="mb-2">Summary</div>
					<AntdInput className="mb-3" value={jira_details ? jira_details.summary : ''} readOnly/>
					<div className="mb-2">Description</div>
					<textarea readOnly style={{ height: '100px', width: '100%', borderRadius: '6px', border: '1px solid #cad1d7' }} className='px-2 py-1 mb-4' value={jira_details ? jira_details.description : ''}  placeholder='Type...' />
					<AntdButton type="primary" className="mr-2" disabled={view_mode} onClick={() => set_show_jira_linking_flow(true)} >Link another Jira issue</AntdButton>	
					<AntdButton type="primary" danger className="mr-2" disabled={view_mode} onClick={() => submit(delink_jira_issue, ()=>{}, 'This action will delink the jira issue of this Product Ticket', '','')} >Delink Jira issue</AntdButton>	
				</>:
				<>
				{
					show_jira_creation_flow?
					<div>
						<div className="mb-2">Summary</div>
						<AntdInput className="mb-4" value={jira_summary} onChange={(e) => set_jira_summary(e.target.value)}/>
						<div className="mb-2">Description</div>
						<textarea style={{ height: '100px', width: '100%', borderRadius: '6px', border: '1px solid #cad1d7' }} className='px-2 py-1 mb-4' value={jira_description} onChange={(e) => set_jira_description(e.target.value)} placeholder='Type...' />
						<div className="mb-2">Project</div>

						<div style={{zIndex: '100005'}}>
						<Select
							menuPortalTarget={document.body}
							styles={{
							menuPortal: (base) => ({ ...base, zIndex: 100005 }),
							}}
							className="zIndex_100005 mb-4"
							options={jira_project_options}
							value={jira_project}
							onChange={(value) => set_jira_project(value)}
						/>
						</div>
						<div className="mb-2">Issue Type</div>
                        <div style={{zIndex: '100004'}}><Select styles={{menuPortal: (styles) => ({...styles, zIndex: '100004 !important'})}} className="zIndex_100004 mb-4" options={jira_type_options} value={jira_type} onChange={(value) => set_jira_type(value)}/></div>
						<div className="mb-2">Priority</div>
                        <div style={{zIndex: '100003'}}><Select styles={{menuPortal: (styles) => ({...styles, zIndex: '100003 !important'})}} className="zIndex_100003 mb-4" options={jira_priority_options} value={jira_priority} onChange={(value) => set_jira_priority(value)}/></div>
						<div className="mb-2">Assignee</div>
                        <div style={{zIndex: '100002'}}><Select styles={{menuPortal: (styles) => ({...styles, zIndex: '100002 !important'})}} className="zIndex_100002 mb-4" options={jira_assignee_options} value={jira_assignee} onChange={(value) => set_jira_assignee(value)}/></div>
						<div className="mb-2">Sprint</div>
                        <div style={{zIndex: '100001'}}><Select styles={{menuPortal: (styles) => ({...styles, zIndex: '100001 !important'})}} className="zIndex_100001 mb-4" options={jira_sprint_options} value={jira_sprint} onChange={(value) => set_jira_sprint(value)}/></div>
						<div className="flex_property">
							<AntdButton type="primary" className="mr-2" onClick={create_jira} >Submit</AntdButton>	
							<div className="mr-2">or</div>
							<AntdButton type="primary" onClick={() => {set_show_jira_creation_flow(false); set_show_jira_linking_flow(true)}} >Link Existing Issue</AntdButton>	
						</div>
					</div>:(
						show_jira_linking_flow?
						<div>
							<div className="mb-2">Jira Issue Key</div>
							<AntdInput className="mb-4" value={jira_key} onChange={(e) => set_jira_key(e.target.value)}/>
							<div className="flex_property">
								<AntdButton type="primary" className="mr-2" onClick={link_existing_jira} >Link this Issue</AntdButton>	
								<div className="mr-2">or</div>
								<AntdButton type="primary" onClick={() => {open_jira_creation_flow(); set_show_jira_linking_flow(false)}} >Create New Issue</AntdButton>	
							</div>
						</div>:
						<div className="flex_center mt-4" style={{}}>
							<AntdButton type="primary" icon={<PlusCircleOutlined style={{fontWeight:700}} />} disabled={view_mode} onClick={() => open_jira_creation_flow()}>Create Jira Issue</AntdButton>
							{'\xa0\xa0\xa0or\xa0\xa0\xa0'}
							<AntdButton type="primary" icon={<PlusCircleOutlined style={{fontWeight:700}} />} disabled={view_mode} onClick={() => set_show_jira_linking_flow(true)}>Link Existing Issue</AntdButton>
						</div>
					)
				}
				{
					selected_ticket && selected_ticket.jira_key?
					<AntdButton className="mt-4" type="dashed" onClick={() => {set_show_jira_creation_flow(false); set_show_jira_linking_flow(false)}} icon={<ArrowLeftOutlined style={{fontWeight:700}}/>}>Go Back</AntdButton>:""
				}
				</>
			)
		)

	const props = {
		name: 'file',
		multiple: true,
		action: '',
		customRequest: ({ fileList, onSuccess }) => { onSuccess() },
		defaultFiles: new_files && new_files.length ? new_files.map((x, idx) => { return { uid: x.id, name: ('file_' + (idx + 1) + " (click to view)"), status: 'done'} }) : [],
		onChange(info) {
			const { status } = info.file;
			set_new_files(info.fileList)

			// Initialize file descriptions for new files and remove orphaned ones
			set_new_file_descriptions(prev => {
				const newDescriptions = {};
				info.fileList.forEach((file) => {
					// Keep existing description or initialize with empty string
					newDescriptions[file.uid] = prev[file.uid] || '';
				});
				return newDescriptions;
			});

			//   if (status !== 'uploading') {
			// 	console.log(info.file, info.fileList);
			//   }
			//   if (status === 'done') {
			// 	message.success(`${info.file.name} file uploaded successfully.`);
			//   } else if (status === 'error') {
			// 	message.error(`${info.file.name} file upload failed.`);
			//   }
		},
		onDrop(e) {
			// Files dropped
		},
	};

	const DriveLinks = () => (
		<div className="flex_center mb-4" style={{flexDirection:'column'}}>
		{
			new_drive_links && new_drive_links.length ? new_drive_links.map((link,idx) => {
				const trimmedLink = link ? link.trim() : '';
				const hasLink = trimmedLink !== '';
				const description = (new_drive_link_descriptions[idx] || '').trim();
				const hasDescription = description !== '';
				const showValidation = hasLink && !hasDescription;

				return (
					<div key={new_drive_link_ids[idx]} style={{width:'100%', marginBottom: '16px'}}>
						<div className="flex_property" style={{width:'100%'}}>
							<Input
								type='text'
								style={{ height: '36px' }}
								className='px-2 py-1 mb-2'
								placeholder='Enter link URL...'
								value={link}
								onChange={(e) => {
									const newLinks = [...new_drive_links];
									newLinks[idx] = e.target.value;
									set_new_drive_links(newLinks);
								}}
							/>
							{
								new_drive_links && new_drive_links.length>1 ?
								<MinusCircleOutlined
									className="ml-2 cp"
									onClick={() => {
										// Use functional setState for atomicity
										set_new_drive_links(prevLinks => prevLinks.filter((_, index) => index !== idx));
										set_new_drive_link_ids(prevIds => prevIds.filter((_, index) => index !== idx));
										set_new_drive_link_descriptions(prev => {
											// Build a reindexed descriptions object that matches the
											// new link indices after removing the entry at `idx`.
											const entries = Object.keys(prev)
												.map(key => ({
													key: parseInt(key, 10),
													value: prev[key],
												}))
												.filter(entry => entry.key !== idx)
												.sort((a, b) => a.key - b.key);

											const reindexed = {};
											entries.forEach((entry, newIndex) => {
												reindexed[newIndex] = entry.value;
											});
											return reindexed;
										});
									}}
								/>:""
							}
						</div>
						{hasLink && (
							<div style={{
								padding: '8px',
								borderRadius: '4px',
								border: showValidation ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
								backgroundColor: showValidation ? '#fff1f0' : 'transparent'
							}}>
								<AntdInput
									type="text"
									placeholder="Link description is required *"
									value={new_drive_link_descriptions[idx] || ''}
									onChange={(e) => {
										const value = e.target.value;
										// Validate length
										if (value.length > MAX_DESCRIPTION_LENGTH) {
											toastr.warning(`Description too long (max ${MAX_DESCRIPTION_LENGTH} characters)`, {
												timeOut: 3000
											});
											return;
										}
										set_new_drive_link_descriptions(prev => ({
											...prev,
											[idx]: value
										}));
									}}
									style={{
										borderColor: showValidation ? '#ff4d4f' : undefined
									}}
									maxLength={MAX_DESCRIPTION_LENGTH}
								/>
								{showValidation && (
									<div style={{
										fontSize: '12px',
										color: '#ff4d4f',
										marginTop: '4px',
										fontWeight: 'bold'
									}}>
										⚠️ Description required
									</div>
								)}
								{description && (
									<div style={{
										fontSize: '11px',
										color: '#888',
										marginTop: '2px',
										textAlign: 'right'
									}}>
										{description.length}/{MAX_DESCRIPTION_LENGTH}
									</div>
								)}
							</div>
						)}
					</div>
				);
			}):''
		}
		<AntdButton type="dashed" className="flex_center" onClick={() => {
			set_new_drive_links([...new_drive_links, '']);
			set_new_drive_link_ids([...new_drive_link_ids, generateUniqueId()]);
		}}><PlusOutlined /> Add Link</AntdButton>
		</div>
	)

	const FilesTab = useCallback(() => (
		<div>
			{/* <Dragger {...props} style={{ display: 'block' }}>
				<p className="ant-upload-drag-icon">
					<InboxOutlined />
				</p>
				<p className="ant-upload-text">Click or drag file to this area to upload</p>
				<p className="ant-upload-hint">
					Supports a single or bulk upload.
				</p>
			</Dragger>
			{new_files && new_files.length && !view_mode ? <Button className="mt-4" color='primary' onClick={add_ticket_files}>Update Files</Button> : ''} */}
			<div className="mb-2 bold">Files</div>
			<div className="mb-4">
			{
				existing_files && existing_files.length && existing_files.filter(file => !file.global_path).length
					? existing_files.filter(file => !file.global_path).map((file, idx) => (
						<FileItem
							key={file.id}
							file={file}
							idx={idx}
							isGlobalPath={false}
							viewMode={view_mode}
							editingFileId={editing_file_id}
							editDescription={edit_description}
							onEditStart={(fileId, description) => {
								set_editing_file_id(fileId);
								set_edit_description(description);
							}}
							onEditCancel={() => {
								set_editing_file_id(null);
								set_edit_description('');
							}}
							onEditSave={update_file_description}
							onEditDescriptionChange={set_edit_description}
							onDelete={delete_file}
							isAnyFileBeingEdited={!!editing_file_id}
						/>
					))
					: <div style={{opacity:'0.4'}}>No files were uploaded</div>
			}
			</div>
			<div className="mb-2 bold">Links</div>
			<div className="mb-4">
			{
				existing_files && existing_files.length && existing_files.filter(file => file.global_path).length
					? existing_files.filter(file => file.global_path).map((file, idx) => (
						<FileItem
							key={file.id}
							file={file}
							idx={idx}
							isGlobalPath={true}
							viewMode={view_mode}
							editingFileId={editing_file_id}
							editDescription={edit_description}
							onEditStart={(fileId, description) => {
								set_editing_file_id(fileId);
								set_edit_description(description);
							}}
							onEditCancel={() => {
								set_editing_file_id(null);
								set_edit_description('');
							}}
							onEditSave={update_file_description}
							onEditDescriptionChange={set_edit_description}
							onDelete={delete_file}
							isAnyFileBeingEdited={!!editing_file_id}
						/>
					))
					: <div style={{opacity:'0.4'}}>No Links were attached</div>
			}
			</div>
			{
				view_mode ? '':
				<>
					<div className="mb-4">
						<div className="mb-2 bold">Add new Files</div>
						<Dragger {...props} fileList={new_files} showUploadList={false} style={{ display: 'block' }}>
							<p className="ant-upload-drag-icon">
								<InboxOutlined />
							</p>
							<p className="ant-upload-text">Click or drag file to this area to upload</p>
							<p className="ant-upload-hint">
								Supports a single or bulk upload.
							</p>
						</Dragger>
					</div>
					{new_files && new_files.length > 0 && (
						<div className="mb-4">
							<div className="mb-2 bold" style={{color: '#ff4d4f'}}>File Descriptions (Required *)</div>
							{new_files.map((file, idx) => {
								const hasDescription = (new_file_descriptions[file.uid] || '').trim() !== '';
								return (
									<div key={file.uid} className="mb-3" style={{
										padding: '12px',
										borderRadius: '4px',
										border: !hasDescription ? '2px solid #ff4d4f' : '1px solid #d9d9d9',
										backgroundColor: !hasDescription ? '#fff1f0' : 'transparent'
									}}>
										<div style={{
											fontSize: '13px',
											marginBottom: '6px',
											fontWeight: '500',
											color: !hasDescription ? '#ff4d4f' : 'rgba(0, 0, 0, 0.85)'
										}}>
											{file.name} {!hasDescription && <span style={{color: '#ff4d4f'}}>⚠️</span>}
										</div>
										<AntdInput
											type="text"
											placeholder="Description is required *"
											value={new_file_descriptions[file.uid] || ''}
											onChange={(e) => {
												const value = e.target.value;
												const uid = file.uid;
												// Validate length
												if (value.length > MAX_DESCRIPTION_LENGTH) {
													toastr.warning(`Description too long (max ${MAX_DESCRIPTION_LENGTH} characters)`, {
														timeOut: 3000
													});
													return;
												}
												set_new_file_descriptions(prev => ({
													...prev,
													[uid]: value
												}));
											}}
											style={{
												height: '36px',
												borderColor: !hasDescription ? '#ff4d4f' : undefined
											}}
											maxLength={MAX_DESCRIPTION_LENGTH}
										/>
										{!hasDescription && (
											<div style={{fontSize: '11px', color: '#ff4d4f', marginTop: '4px'}}>
												Description is required before submitting
											</div>
										)}
										{(new_file_descriptions[file.uid] || '').trim() && (
											<div style={{
												fontSize: '11px',
												color: '#888',
												marginTop: '2px',
												textAlign: 'right'
											}}>
												{(new_file_descriptions[file.uid] || '').length}/{MAX_DESCRIPTION_LENGTH}
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
					{
						new_files && new_files.length ? (
							<div className="mb-4">
								<AntdButton type="primary" onClick={add_ticket_files}>Submit Files</AntdButton>
							</div>
						) : ''
					}
					<div className="mb-4">
						<div className="mb-2 bold">Add new Links</div>
						{DriveLinks()}
						{
							new_drive_links && new_drive_links.length ? <AntdButton type="primary" onClick={add_drive_links}>Submit Links</AntdButton> : ''
						}
					</div>
                    {
                        selected_ticket && selected_ticket.type != 'bug' ? '' :
                            <div className="mb-4">
                                <div className="mb-2 bold">Checklist</div>
                                {
                                    <div className="flex_center mb-4" style={{flexDirection:'column'}}>
                                        {
                                            Object.keys(ticketChecklist).map((key, idx) => (
                                                <div className="flex_property" style={{width:'100%'}}>
                                                    <Checkbox checked={checklist[key]} onClick={() => {update_checklist_status(key)}}>
                                                        <div>{ticketChecklist[key]}</div>
                                                    </Checkbox>
                                                </div>
                                            ))
                                        }
                                    </div>

                                }
                        </div>
                    }
				</>
			}
		</div>
	), [existing_files, new_files, new_file_descriptions, new_drive_links, new_drive_link_descriptions, new_drive_link_ids, selected_ticket, view_mode, checklist, editing_file_id, edit_description])

//	const LinkedBackOfficeTicket = () => (
//		selected_ticket && selected_ticket.back_office_ticket ?
//			<>
//				<Badge.Ribbon text={selected_ticket.back_office_ticket.type === 'bug' ? 'Bug' : (selected_ticket.back_office_ticket.type === 'feature' ? 'Feature' : '')} style={{ fontSize: '12px', display: !['bug', 'feature'].includes(selected_ticket.back_office_ticket.type) ? 'none' : '' }} color={selected_ticket.back_office_ticket.type === 'bug' ? 'red' : (selected_ticket.back_office_ticket.type === 'feature' ? 'green' : '')}>
//					<div className="activity_box mb-4" style={{ padding: '0px' }} >
//						<div style={{ padding: "18px 18px 10px 18px" }}>
//							<div className="flex_between mb-2" style={{ fontSize: '16px', cursor: "pointer" }} onClick={() => select_back_office_ticket(selected_ticket.back_office_ticket.id)}>
//								<div className="flex_property">
//									<i className="fa fa-ticket mr-2" style={{ fontSize: '20px', color: '#A799B7' }} />
//									<div>{selected_ticket.back_office_ticket.title}</div>
//								</div>
//							</div>
//							<div style={{ fontSize: '12px', marginLeft: '28px' }} className="flex_between">
//								<div className="flex_property">
//									{
//										selected_ticket.back_office_ticket.jira_key?
//										<div className="mr-2 flex_property">
//											<a href={`https://infurniacoretech.atlassian.net/jira/software/c/projects/PT/issues/${selected_ticket.back_office_ticket.jira_key}`} target="_blank" className="mr-2 flex_property" style={{textDecoration:'underline', color:'#0078ff'}}><img src='/jira.svg' height={'14px'} width={'14px'} className="mr-1"/><div>{selected_ticket.back_office_ticket.jira_key}</div></a>
//											|
//										</div>:""
//									}
//									{
//										selected_ticket.back_office_ticket.priority ?
//											<div className="mr-2 flex_property">
//												<div className="mr-2">
//													Priority - {selected_ticket.back_office_ticket.priority}
//												</div>
//												|
//											</div> : ""
//									}
//									{
//										/* selected_ticket.back_office_ticket.customer_priority ?
//											<div className="mr-2 flex_property">
//												<div className="mr-2">
//													Customer Priority - {selected_ticket.back_office_ticket.customer_priority}
//												</div>
//												|
//											</div> : "" */
//									}
//									<div className="mr-2" style={{ opacity: '0.5' }}>
//										<i style={{ color: '' }} className="fa fa-history mr-1" />
//										Updated on {date_string(selected_ticket.back_office_ticket.updated_at)}
//									</div>
//								</div>
//
//							</div>
//						</div>
//						<hr className="my-0" />
//						<div className="cp flex_between" style={{ fontSize: '13px', borderRadius: '6px', padding: '6px 10px 6px 18px', }}>
//							<div>
//								<i className="far fa-building mr-1" />{convert_to_readable(selected_ticket.back_office_ticket.department)}
//							</div>
//							{/* <div className="flex_center" style={{color:selected_ticket.back_office_ticket.type==='bug'?'red':(selected_ticket.back_office_ticket.type==='feature'?'green':'')}}>
//										{
//											selected_ticket.back_office_ticket.type==='bug'?
//											<BugOutlined className="mr-1"/>:(
//												selected_ticket.back_office_ticket.type==='feature'?
//												<StarOutlined className="mr-1" />:
//												<RobotOutlined className="mr-1"/>
//											)
//										}
//										{selected_ticket.back_office_ticket.type ? convert_to_readable(selected_ticket.back_office_ticket.type) : 'No type'}
//									</div> */}
//							<div>
//								<i className="far fa-user mr-1" />{cs_org_users && cs_org_users.length && selected_ticket.back_office_ticket.assignee_id ? cs_org_users.find(o => o.id === selected_ticket.back_office_ticket.assignee_id).first_name : 'Not Assigned'}
//							</div>
//							<div className="flex_center" title="Expected resolution time">
//								<i className="far fa-clock mr-1" />{selected_ticket.back_office_ticket.expected_resolution_time ? (selected_ticket.back_office_ticket.expected_resolution_time + ' days') : <NA/>}
//							</div>
//							<div>
//								<Input disabled onClick={(e) => e.stopPropagation()} type='select' style={{ width: '150px', height: '32px', padding: '4px 12px', }}><option>{selected_ticket && selected_ticket.back_office_ticket && bo_tickets_status_options.find(o => o.value===selected_ticket.back_office_ticket.status) ? bo_tickets_status_options.find(o => o.value===selected_ticket.back_office_ticket.status).label : ''}</option></Input>
//							</div>
//						</div>
//					</div>
//				</Badge.Ribbon>
//				{
//					<div>
//						{/* <div className="mb-1 bold">Additional Info:</div> */}
//						{
//							// selected_ticket.back_office_ticket.department === 'product' ?
//							<>
//								{
//									selected_ticket.back_office_ticket.type === 'feature' ?
//										<>
//											<div className='mb-2 bold'>Use Case</div>
//											<textarea readOnly style={{ height: '100px', width: '100%', borderRadius: '6px', border: '1px solid #cad1d7' }} className='px-2 py-1 mb-4' value={JSON.parse(selected_ticket.back_office_ticket.additional_info).use_case} placeholder='e.g. The client is trying to create shutters with a design that is shown in this image link <url>' />
//										</> : (
//											selected_ticket.back_office_ticket.type === 'bug' ?
//												<>
//													<div className='mb-2 bold'>Reproduction</div>
//													<textarea readOnly style={{ height: '100px', width: '100%', borderRadius: '6px', border: '1px solid #cad1d7' }} className='px-2 py-1 mb-4' value={JSON.parse(selected_ticket.back_office_ticket.additional_info).reproduction} placeholder='e.g. Add a Cabinet, Change the shutter and external finish and then change shutter design from normal to profile' />
//													{/* <div className='mb-2 bold'>Comments</div>
//													<textarea readOnly style={{ height: '100px', width: '100%', borderRadius: '6px', border: '1px solid #cad1d7' }} className='px-2 py-1 mb-4' value={JSON.parse(selected_ticket.back_office_ticket.additional_info).comments} placeholder='e.g. the same shutter related bug was also noticed at partition level also' /> */}
//												</> : ""
//										)
//								}
//							</> 
//                                /* :
//							<>
//								<label className="form-control-label mt-2 " htmlFor="input-address" style={{ minWidth: '60%', marginBottom: 0 }}>
//									<div className='mr-2 bold'>Global/Org</div>
//									<div className="flex_property mb-4">
//										<div style={{ minHeight: '32px', fontWeight: 400 }} className="ml-2 mr-2 flex_property" >
//											<input disabled type="radio" className="mr-2" checked={selected_ticket.back_office_ticket.type === 'org' ? true : false} />
//											<div className="mr-4">Org</div>
//										</div>
//										<div style={{ minHeight: '32px', fontWeight: 400 }} className="ml-2 mr-2 flex_property" >
//											<input disabled type="radio" className="mr-2" checked={selected_ticket.back_office_ticket.type === 'global' ? true : false} />
//											<div className="mr-4">Global</div>
//										</div>
//										<div style={{ minHeight: '32px', fontWeight: 400 }} className="ml-2 mr-2 flex_property" >
//											<input disabled type="radio" className="mr-2" checked={selected_ticket.back_office_ticket.type === 'org_and_global' ? true : false} />
//											<div>Both</div>
//										</div>
//									</div>
//								</label>
//							</> */
//						}
//					</div>
//				}
//				<div className="mb-3 bold">Comments <Tooltip title='Refresh comments'><AntdButton className="ml-1 px-2 py-1"><span className="cp" onClick={() => {console.log('refreshhhh'); get_comments()}} style={{opacity:'0.5'}}><i className="fa fa-refresh"/></span></AntdButton></Tooltip></div>
//				<div className="mb-4" style={{display:'flex'}}>
//					<div className="avatar_div mr-2">{user_details && user_details.first_name ? user_details.first_name[0] : ':)'}</div>
//					<div style={{width:"100%"}}>
//						<textarea value={new_comment} onChange={(e) => set_new_comment(e.target.value)} style={{ height: '100px', width: '100%', borderRadius: '6px', border: '1px solid #cad1d7' }} className='px-2 py-1' placeholder="Add a comment..."/>
//						{
//							new_comment?
//							<div>
//								<AntdButton onClick={() => set_new_comment('')} className="mr-2">Cancel</AntdButton>
//								<AntdButton onClick={add_comment} type="primary">Save</AntdButton>
//							</div>:""
//						}
//					</div>
//				</div>
//				{
//					bo_ticket_comments && bo_ticket_comments.length ? bo_ticket_comments.map((single_comment, idx) => (
//						<div className="mb-4" style={{display:'flex'}}>
//							<div className="avatar_div mr-2">{single_comment.commented_by && cs_org_users && cs_org_users.length ? cs_org_users.find(o => o.id===single_comment.commented_by).first_name[0] : ':)'}</div>
//							<div style={{width:"100%"}}>
//								<div className="flex_property mb-2">
//									<div className="mr-2" style={{color:'#44546f', fontWeight:500}}>{single_comment.commented_by && cs_org_users && cs_org_users.length ? cs_org_users.find(o => o.id===single_comment.commented_by).first_name : 'Anonymous User'}</div>
//									<div style={{opacity:'0.5'}}>{timeSince(single_comment.updated_at)}</div>
//								</div>
//								<div>{single_comment.comment}</div>
//								{user_details && single_comment.commented_by===user_details.id ? 
//									<div>
//
//									</div>:''
//								}
//							</div>
//						</div>
//					)):''
//				}
//			</> : 'No Linked Product Ticket'
//	)

	const tab_items = [
		{
			key: '1',
			label: 'Details',
			children: TicketDetails(),
		},
		{
			key: '2',
			label: 'Files / Links',
			children: FilesTab(),
		},
		{
			key: '3',
			label: 'Jira Ticket',
			children: JiraTab(),
		},
		{
			key: '4',
			label: 'Comments',
			children: CommentsTab(),
		},
	];

	useEffect(() => {
		// Track file changes
	}, [new_files]);

	const callbackTabClicked = (key, event) => {
		set_active_key(key)
		if(key === '3'){
			if(selected_ticket && selected_ticket.jira_key && !jira_details) get_jira_details()
		}
	}

	// const fetch_back_office_tickets = async() => {
	// 	try{
	// 		let user_department 
	// 		if(user_details && user_details.role && user_details.role.store && user_details.role.store.length){
	// 			if(user_details.role.store.includes('customer_success_admin')){
	// 				user_department = 'all'
	// 			}else if(user_details.role.store.includes('cs_rep')){
	// 				user_department = 'customer_success'
	// 			}else if(user_details.role.store.includes('infurnia_product_manager')){
	// 				user_department = 'product'
	// 			}else if(user_details.role.store.includes('infurnia_catalog_admin')){
	// 				user_department = 'catalogue'
	// 			}
	// 		}
	// 		let body = {department: user_department}
	// 		if(link_mode) body['department'] = link_mode_department
	// 		if(!body.department){
	// 			set_initialized(true)
	// 			return
	// 		}
	// 		let resp = await general_fetch({url:'back_office_ticket/get_by_department', body})
	// 		set_back_office_tickets(resp)
	// 		// set_display_back_office_tickets(resp)
	// 		set_initialized(true)
	// 	}catch(err){
	// 		console.error(err)
	// 	}
	// }

	return (
		<Drawer title={(view_mode ? "Viewing" : "Edit") + " Product Ticket"} onClose={onclick_handleclose} open={open} size="large" bodyStyle={{ paddingTop: '0px' }}>
            {
                // <EditBackOffficeTicket 
                //     open={show_back_office_ticket_drawer}
                //     toggle={() => set_show_back_office_ticket_drawer(!show_back_office_ticket_drawer)}
                //     selected_ticket_id={selected_back_office_ticket}
                //     fetch_back_off_tickets={() => selected_ticket ? [selected_ticket.back_office_ticket] : null}
                //     set_page_loader={set_page_loader}
                //     cs_org_users={cs_org_users}
                //     // all_stores={all_stores}
                //     set_view_mode_true={set_view_mode_true}
                //     user_details={user_details}
                //     update_status={() => 1}
                //     exclude_customer_ticket_drawer={true}
                // />
            }
			<Tabs activeKey={active_key} items={tab_items} onTabClick={callbackTabClicked} />
			<CommentModal 
				open={show_comment_modal} 
				toggle={() => set_show_comment_modal(false)}
				onSubmit={handle_comment_submit}
			/>
		</Drawer>
	)
}

export {EditCustomerTicket, check_ticket_edit_access}
